import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Send, AlertCircle, CheckCheck, Clock, Paperclip, X, FileText, Image as ImageIcon, File, Search } from 'lucide-react';
import { saveAs } from 'file-saver';
import Navigation from '../components/Navigation';
import { MentionInput } from '../components/MentionInput';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { trackFeatureUsage, trackMessageSent } from '../utils/analytics';
import { linkifyText } from '../utils/linkify';
import { formatDateTimeShort } from '../utils/dateFormat';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

function Messages({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [mentionIds, setMentionIds] = useState([]);
  const [priority, setPriority] = useState('medium');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const messageTextareaRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  // Filter messages based on search query
  const filteredMessages = useMemo(() => {
    const sorted = [...messages].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    if (!searchQuery.trim()) return sorted;

    const query = searchQuery.toLowerCase();
    return sorted.filter(msg =>
      msg.content?.toLowerCase().includes(query) ||
      msg.sender_name?.toLowerCase().includes(query) ||
      msg.recipient_name?.toLowerCase().includes(query)
    );
  }, [messages, searchQuery]);

  // Highlight search matches in text
  const highlightText = (text, isMyMessage = false) => {
    if (!searchQuery.trim() || !text) return text;
    
    const query = searchQuery.toLowerCase();
    const index = text.toLowerCase().indexOf(query);
    
    if (index === -1) return text;
    
    const before = text.slice(0, index);
    const match = text.slice(index, index + searchQuery.length);
    const after = text.slice(index + searchQuery.length);
    
    return (
      <>
        {before}
        <mark className={`${isMyMessage ? 'bg-yellow-300 text-gray-900' : 'bg-yellow-200'} px-0.5 rounded`}>
          {match}
        </mark>
        {after}
      </>
    );
  };

  // Render message content with links and @mentions
  const renderMessageContent = (text, isMyMessage = false) => {
    if (!text) return null;
    
    // Split by @mention patterns first
    const mentionRegex = /(@[\w\s]+?)(?=\s@|\s*$|[.,!?;:\)\]\}]|\s{2})/g;
    const segments = [];
    let lastIdx = 0;
    let match;
    const textCopy = text;
    while ((match = mentionRegex.exec(textCopy)) !== null) {
      if (match.index > lastIdx) {
        segments.push({ type: 'text', value: textCopy.slice(lastIdx, match.index) });
      }
      segments.push({ type: 'mention', value: match[0] });
      lastIdx = match.index + match[0].length;
    }
    if (lastIdx < textCopy.length) {
      segments.push({ type: 'text', value: textCopy.slice(lastIdx) });
    }
    
    return segments.map((seg, i) => {
      if (seg.type === 'mention') {
        return (
          <span key={`m-${i}`} className={`font-semibold ${isMyMessage ? 'text-blue-200 bg-white/10' : 'text-blue-600 bg-blue-50'} px-0.5 rounded`}>
            {seg.value}
          </span>
        );
      }
      // For text segments, apply linkify and search highlighting
      const parts = linkifyText(seg.value, isMyMessage);
      return parts.map((part, j) => {
        if (typeof part === 'string') {
          return <span key={`${i}-${j}`}>{highlightText(part, isMyMessage)}</span>;
        } else if (part.type === 'link') {
          return (
            <a
              key={`${i}-${j}`}
              href={part.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`underline break-all ${isMyMessage ? 'text-white hover:text-white/80' : 'text-[#80298F] hover:text-[#6B2278]'}`}
            >
              {part.url}
            </a>
          );
        }
        return null;
      });
    });
  };

  useEffect(() => {
    fetchData();
    // Track messages page visit
    trackFeatureUsage('Messages');
  }, []);

  // Watch for URL parameter changes (e.g., when clicking notification while on messages page)
  useEffect(() => {
    const caseIdFromUrl = searchParams.get('case');
    if (caseIdFromUrl && cases.length > 0) {
      const caseFromUrl = cases.find(c => c.case_id === caseIdFromUrl);
      if (caseFromUrl && (!selectedCase || selectedCase.case_id !== caseIdFromUrl)) {
        setSelectedCase(caseFromUrl);
      }
    }
  }, [searchParams, cases]);

  useEffect(() => {
    if (selectedCase) {
      fetchMessages(selectedCase.case_id);
    }
  }, [selectedCase]);

  const fetchData = async () => {
    try {
      const [casesRes, contactsRes, clientsRes] = await Promise.all([
        fetch(`${API}/cases`, { credentials: 'include' }),
        fetch(`${API}/users/case-contacts`, { credentials: 'include' }),
        fetch(`${API}/users/clients`, { credentials: 'include' })
      ]);

      if (casesRes.ok) {
        const casesData = await casesRes.json();
        setCases(casesData);
        
        // Check if there's a case ID in the URL query params
        const caseIdFromUrl = searchParams.get('case');
        if (caseIdFromUrl) {
          const caseFromUrl = casesData.find(c => c.case_id === caseIdFromUrl);
          if (caseFromUrl) {
            setSelectedCase(caseFromUrl);
          }
        } else if (casesData.length > 0 && user.role === 'client') {
          // Only auto-select for clients who typically have one case
          setSelectedCase(casesData[0]);
        }
      }

      if (contactsRes.ok) {
        const contactsData = await contactsRes.json();
        setUsers(contactsData);
      }
      
      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (caseId) => {
    try {
      const response = await fetch(`${API}/messages/case/${caseId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        
        // Mark unread messages as read
        const unreadMessages = data.filter(m => !m.read && m.recipient_id === user.user_id);
        for (const msg of unreadMessages) {
          await fetch(`${API}/messages/${msg.message_id}/read`, {
            method: 'PATCH',
            credentials: 'include'
          });
        }
      }
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('case_id', selectedCase.case_id);
      
      // Auto-set recipient based on user role:
      // - Clients: send to "staff_inbox" (all staff can see)
      // - Staff/Admin: send to the case's client
      if (user.role === 'client') {
        formData.append('recipient_id', 'staff_inbox');
      } else {
        formData.append('recipient_id', selectedCase.client_id);
      }
      
      formData.append('content', newMessage);
      formData.append('priority', priority);
      if (mentionIds.length > 0) {
        formData.append('mention_ids', mentionIds.join(','));
      }
      
      // Add file attachments
      attachments.forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch(`${API}/messages`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) throw new Error('Failed to send message');
      
      // Track message sent for analytics
      trackMessageSent(selectedCase.case_id);
      
      toast.success('Message sent successfully');
      setNewMessage('');
      setMentionIds([]);
      setPriority('medium');
      setAttachments([]);
      fetchMessages(selectedCase.case_id);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });
    
    setAttachments(prev => [...prev, ...validFiles]);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <ImageIcon className="h-4 w-4" />;
    } else if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
      return <FileText className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  const handleDownloadAttachment = async (fileId, fileName) => {
    try {
      // Fetch the file with credentials
      const response = await fetch(`${API}/messages/attachment/${fileId}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Show clearer message for file not found
        if (response.status === 404) {
          toast.error('This file is no longer available. It may have been removed during a system update. Please ask the sender to re-upload.');
          return;
        }
        throw new Error(errorData.detail || 'Failed to download attachment');
      }
      
      // Get the blob and trigger download
      const blob = await response.blob();
      saveAs(blob, fileName);
      
      toast.success('Download complete');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download attachment');
    }
  };

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/login');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-[#DC3239] bg-[#DC3239]/10';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-[#FAA519] bg-[#FAA519]/10';
      case 'low': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (cases.length === 0) {
    return (
      <div className="min-h-screen bg-[#FDFCFE]">
        <Navigation user={user} onLogout={handleLogout} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No cases available for messaging</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900" data-testid="messages-heading">Messages</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Secure communication for your cases</p>
        </div>

        {/* Case Selector - Only show for clients at the top level */}
        {user.role === 'client' && (
          <div className="mb-4 sm:mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Case</label>
            <Select 
              value={selectedCase?.case_id} 
              onValueChange={(caseId) => {
                const selected = cases.find(c => c.case_id === caseId);
                setSelectedCase(selected);
              }}
            >
              <SelectTrigger data-testid="case-select" className="h-auto min-h-[2.5rem] py-2 [&>span]:whitespace-normal [&>span]:!line-clamp-none [&>span]:text-left [&>span]:break-words [&>span]:pr-2">
                <SelectValue placeholder="Select a case..." />
              </SelectTrigger>
              <SelectContent className="max-w-[calc(100vw-2rem)]">
                {cases.map(c => (
                  <SelectItem key={c.case_id} value={c.case_id} className="whitespace-normal">
                    {c.case_name} ({c.case_number})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Messages List - First on mobile, first on desktop */}
          <div className="lg:col-span-2 order-1">
            <Card>
              <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="text-lg">Conversation</CardTitle>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search messages..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9"
                      data-testid="message-search-input"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                {searchQuery && (
                  <p className="text-sm text-gray-500 mt-2">
                    {filteredMessages.length} {filteredMessages.length === 1 ? 'result' : 'results'} found
                  </p>
                )}
              </CardHeader>
              <CardContent className="px-2 sm:px-6 py-2 sm:py-4">
                {!selectedCase ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Select a case to view messages</p>
                  </div>
                ) : filteredMessages.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      {searchQuery 
                        ? `No messages matching "${searchQuery}"` 
                        : 'No messages yet. Start the conversation!'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto px-2" data-testid="messages-list">
                    {filteredMessages.map((message) => {
                      // For staff/admin viewing: staff messages on right, client messages on left
                      // For client viewing: client's own messages on right, staff messages on left
                      const isStaffOrAdminMessage = message.sender_role === 'staff' || message.sender_role === 'admin';
                      const isMyMessage = user.role === 'client' 
                        ? message.sender_id === user.user_id  // Clients see their own messages on right
                        : isStaffOrAdminMessage;  // Staff/Admin see staff messages on right
                      return (
                        <div
                          key={message.message_id}
                          className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[85%] sm:max-w-[75%] rounded-2xl p-3 sm:p-4 ${
                            isMyMessage 
                              ? 'bg-[#80298F] text-white rounded-br-md' 
                              : 'bg-gray-100 text-gray-900 rounded-bl-md'
                          }`}>
                            {/* Sender info */}
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <p className={`text-xs sm:text-sm font-semibold ${isMyMessage ? 'text-white' : 'text-gray-900'}`}>
                                {message.sender_id === user.user_id ? 'You' : highlightText(message.sender_name, isMyMessage)}
                              </p>
                              <span className={`text-xs ${isMyMessage ? 'text-white/60' : 'text-gray-500'}`}>
                                {message.sender_role}
                              </span>
                              {message.priority !== 'medium' && (
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                  isMyMessage ? 'bg-white/20 text-white' : getPriorityColor(message.priority)
                                }`}>
                                  {message.priority}
                                </span>
                              )}
                            </div>
                            
                            {/* Message content */}
                            <p className={`text-sm whitespace-pre-wrap break-words overflow-hidden ${isMyMessage ? 'text-white' : 'text-gray-700'}`} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                              {renderMessageContent(message.content, isMyMessage)}
                            </p>
                            
                            {/* Attachments */}
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {message.attachments.map((attachment, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => handleDownloadAttachment(attachment.file_id, attachment.file_name)}
                                    className={`w-full flex items-center space-x-2 p-2 rounded border ${
                                      isMyMessage 
                                        ? 'bg-white/10 border-white/20 hover:bg-white/20' 
                                        : 'bg-white border-gray-200 hover:bg-gray-50'
                                    } transition-colors cursor-pointer`}
                                    data-testid={`attachment-${attachment.file_id}`}
                                  >
                                    {getFileIcon(attachment.file_name)}
                                    <span className={`text-xs flex-1 truncate text-left ${isMyMessage ? 'text-white' : 'text-gray-700'}`}>
                                      {attachment.file_name}
                                    </span>
                                    <Paperclip className={`h-3 w-3 ${isMyMessage ? 'text-white/70' : 'text-gray-400'}`} />
                                  </button>
                                ))}
                              </div>
                            )}
                            
                            {/* Footer with time and actions */}
                            <div className={`flex items-center justify-between mt-2 pt-2 border-t ${isMyMessage ? 'border-white/20' : 'border-gray-200'}`}>
                              <span className={`text-xs ${isMyMessage ? 'text-white/60' : 'text-gray-400'}`}>
                                {message.formatted_time || formatDateTimeShort(message.created_at)}
                              </span>
                              <div className="flex items-center gap-2">
                                {message.read ? (
                                  <CheckCheck className={`h-4 w-4 ${isMyMessage ? 'text-white/70' : 'text-gray-400'}`} />
                                ) : (
                                  <Clock className={`h-4 w-4 ${isMyMessage ? 'text-white/70' : 'text-gray-400'}`} />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Send Message Form - Second on mobile, sidebar on desktop */}
          <div className="order-2">
            <Card>
              <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
                <CardTitle className="text-lg">Send Message</CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6 py-2 sm:py-4">
                <form onSubmit={handleSendMessage} className="space-y-3 sm:space-y-4">
                  {/* Case Selection - Inside the form for staff/admin */}
                  {user.role !== 'client' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Case
                      </label>
                      <Select 
                        value={selectedCase?.case_id || ''} 
                        onValueChange={(caseId) => {
                          const selected = cases.find(c => c.case_id === caseId);
                          setSelectedCase(selected);
                        }}
                      >
                        <SelectTrigger data-testid="case-select" className="h-auto min-h-[2.5rem] py-2 [&>span]:whitespace-normal [&>span]:!line-clamp-none [&>span]:text-left [&>span]:break-words [&>span]:pr-2">
                          <SelectValue placeholder="Select a case..." />
                        </SelectTrigger>
                        <SelectContent className="max-w-[calc(100vw-2rem)]">
                          {cases.map(c => (
                            <SelectItem key={c.case_id} value={c.case_id} className="whitespace-normal">
                              {c.case_name} ({c.case_number})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      To
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                      {user.role === 'client' 
                        ? 'The Ramirez Legal Group Team' 
                        : selectedCase ? (clients.find(c => c.user_id === selectedCase.client_id)?.name || 'Client') : 'Select a case first'}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger id="priority" data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message {(user.role === 'staff' || user.role === 'admin') && <span className="text-gray-400 font-normal">- Type @ to mention staff</span>}
                    </label>
                    {(user.role === 'staff' || user.role === 'admin') ? (
                      <MentionInput
                        value={newMessage}
                        onChange={setNewMessage}
                        onMentionsChange={setMentionIds}
                        placeholder="Type your message here... Use @name to mention staff"
                        rows={4}
                        disabled={sending}
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                      />
                    ) : (
                      <Textarea
                        id="message"
                        ref={messageTextareaRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message here..."
                        rows={4}
                        required
                        data-testid="message-textarea"
                        className="text-sm"
                      />
                    )}
                  </div>

                  {/* File Attachments */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments (Optional)
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      data-testid="file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                      data-testid="attach-file-button"
                    >
                      <Paperclip className="h-4 w-4 mr-2" />
                      Attach Files
                    </Button>
                    
                    {attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {attachments.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                          >
                            <div className="flex items-center space-x-2 flex-1">
                              {getFileIcon(file.name)}
                              <span className="text-sm text-gray-700 truncate">{file.name}</span>
                              <span className="text-xs text-gray-500">
                                ({(file.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(index)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                              data-testid={`remove-attachment-${index}`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#80298F] hover:bg-[#6B2278]"
                    disabled={sending || !selectedCase}
                    data-testid="send-message-button"
                  >
                    {sending ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>

                {priority === 'urgent' && (
                  <div className="mt-4 p-3 bg-[#DC3239]/10 border border-[#DC3239]/20 rounded-lg flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-[#DC3239] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#DC3239]">
                      This message will be marked as urgent and may trigger immediate notifications.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Messages;